import {
  SplitSupplyNoticeSection,
  type SplitSupplyPartnerNoticeImportError,
} from '@ebalance/proto/bffuser/v1/split_supply_plan_model_pb'
import { useTranslation } from 'react-i18next'

import {
  Alert,
  Flex,
  TertiaryButton,
  Text,
} from '@enechain/ecloud-designsystem'

import { CsvDiffTable } from '~/components/common/CsvUpload/CsvDiffTable'
import { HintTooltip } from '~/components/common/HintTooltip'
import { type CivilDate, protoDateToCivilDate } from '~/models/civilDate'
import { formatDateDisplay } from '~/models/dateTime'

/**
 * 通告値の取込の確認画面 (取込ダイアログの 2 段目) の、 他社の通告値と自社の通告値で同じ部分:
 * ファイルの誤り・取り込まない地点・無視したファイル。 文言の置き場 (`prefix`) と理由の文言の対応だけが
 * 取込の側ごとに違う。
 */

export const NO_VALUE = '—'

/** 通の対象 (翌日は日、 月間は月) の表示。 */
export const importPeriodLabel = (
  section: SplitSupplyNoticeSection,
  target: CivilDate | undefined,
  monthLabel: (year: number, month: number) => string,
): string => {
  if (target === undefined) return NO_VALUE
  return section === SplitSupplyNoticeSection.MONTHLY
    ? monthLabel(target.year, target.month)
    : formatDateDisplay(new Date(target.year, target.month - 1, target.day))
}

/** 地点の件数。 件があれば hover で地点の番号を出す (多い時は先頭だけ)。 */
export const PointCountCell = ({
  count,
  points,
}: {
  count: number
  points: string[]
}): React.ReactElement => {
  const { t } = useTranslation()
  const label = t('page:splitSupplyPlan.pointCount', { count })
  if (count === 0) return <span>{label}</span>
  return (
    <HintTooltip
      content={
        points.length < count ? `${points.join('\n')}\n…` : points.join('\n')
      }
    >
      <span>{label}</span>
    </HintTooltip>
  )
}

type ErrorsProps = {
  /** 文言の置き場 (例 `page:splitSupplyPlan.own.import`)。 */
  prefix: string
  errors: SplitSupplyPartnerNoticeImportError[]
  errorCount: number
  /** 誤りの理由 (enum の値) → 文言の key。 */
  reasonKeys: Record<number, string | undefined>
  /** 誤りがあって取り込めない時の案内。 */
  invalidMessage: string
  /**
   * 誤りのあるファイルを外して確認し直す。 外せるファイルが無い (誤りのファイル名が選んだファイルと
   * 一致しない・外すと何も残らない) 時は undefined で、 ボタンを出さない。
   */
  onRemoveBadFiles: (() => void) | undefined
}

/** ファイルの誤り (件数と先頭の一覧)。 誤りが 1 つでもあれば取り込めない。 */
export const ImportErrorsSection = ({
  prefix,
  errors,
  errorCount,
  reasonKeys,
  invalidMessage,
  onRemoveBadFiles,
}: ErrorsProps): React.ReactElement => {
  const { t } = useTranslation()
  return (
    <Flex flexDirection="column" gap={2}>
      <Alert description={invalidMessage} status="error" />
      <Text textStyle="bodyBold">
        {t(`${prefix}.errors.heading`, { count: errorCount })}
      </Text>
      <CsvDiffTable
        columns={[
          {
            key: 'file',
            header: t(`${prefix}.column.file`),
            cell: (row: SplitSupplyPartnerNoticeImportError) =>
              row.fileName === '' ? NO_VALUE : row.fileName,
          },
          {
            key: 'point',
            header: t(`${prefix}.column.supplyPoint`),
            mono: true,
            cell: (row) => row.supplyPointNumber ?? NO_VALUE,
          },
          {
            key: 'date',
            header: t(`${prefix}.column.date`),
            cell: (row) => {
              const date = protoDateToCivilDate(row.date)
              return date === undefined
                ? NO_VALUE
                : formatDateDisplay(
                    new Date(date.year, date.month - 1, date.day),
                  )
            },
          },
          {
            key: 'slot',
            header: t(`${prefix}.column.slot`),
            align: 'right',
            cell: (row) => row.slot ?? NO_VALUE,
          },
          {
            key: 'reason',
            header: t(`${prefix}.column.reason`),
            cell: (row) => {
              const key = reasonKeys[row.reason]
              return key === undefined ? NO_VALUE : t(key)
            },
          },
        ]}
        // 同じ誤りの行が並びうる (月をまたぐ日の日が同じ等) ので、 並びの位置で区別する
        getRowKey={(row) => String(row.rowIndex)}
        getRowTone={() => 'error'}
        rows={errors.map((error, rowIndex) => ({ ...error, rowIndex }))}
      />
      {errors.length < errorCount && (
        <Text textStyle="caption" color="Text.midEmphasis">
          {t(`${prefix}.errors.truncated`, { shown: errors.length })}
        </Text>
      )}
      {onRemoveBadFiles !== undefined && (
        <Flex>
          <TertiaryButton onClick={onRemoveBadFiles} size="md">
            {t(`${prefix}.errors.removeBad`)}
          </TertiaryButton>
        </Flex>
      )}
    </Flex>
  )
}

type SkippedRow = {
  fileName: string
  supplyPointNumber: string
  reason: number
}

type SkippedProps<Row extends SkippedRow> = {
  prefix: string
  skippedPointCount: number
  skippedPoints: Row[]
  skippedCounts: Array<{ reason: number; count: number }>
  /** 取り込まない理由 (enum の値) → 文言の key。 */
  reasonKeys: Record<number, string | undefined>
}

/** 取り込まない地点 (理由ごとの件数と先頭の一覧)。 */
export const ImportSkippedSection = <Row extends SkippedRow>({
  prefix,
  skippedPointCount,
  skippedPoints,
  skippedCounts,
  reasonKeys,
}: SkippedProps<Row>): React.ReactElement => {
  const { t } = useTranslation()
  return (
    <Flex flexDirection="column" gap={2}>
      <Text textStyle="bodyBold">
        {t(`${prefix}.skipped.heading`, { count: skippedPointCount })}
      </Text>
      <Flex flexDirection="column" gap={0}>
        {skippedCounts.map((c) => {
          const key = reasonKeys[c.reason]
          return key === undefined ? null : (
            <Text key={c.reason} textStyle="caption">
              {`${t(key)}: ${t('page:splitSupplyPlan.pointCount', { count: c.count })}`}
            </Text>
          )
        })}
      </Flex>
      <CsvDiffTable
        columns={[
          {
            key: 'file',
            header: t(`${prefix}.column.file`),
            cell: (row: Row) => row.fileName,
          },
          {
            key: 'point',
            header: t(`${prefix}.column.supplyPoint`),
            mono: true,
            cell: (row) => row.supplyPointNumber,
          },
          {
            key: 'reason',
            header: t(`${prefix}.column.skipReason`),
            cell: (row) => {
              const key = reasonKeys[row.reason]
              return key === undefined ? NO_VALUE : t(key)
            },
          },
        ]}
        // 同じファイルを 2 回選ぶと、 ファイル名・地点・理由が同じ行が 2 つ並ぶ (電文ごとに 1 行) ので、 並びの位置で区別する
        getRowKey={(row) => String(row.rowIndex)}
        getRowTone={() => undefined}
        rows={skippedPoints.map((point, rowIndex) => ({ ...point, rowIndex }))}
      />
      {skippedPoints.length < skippedPointCount && (
        <Text textStyle="caption" color="Text.midEmphasis">
          {t(`${prefix}.skipped.truncated`, { shown: skippedPoints.length })}
        </Text>
      )}
    </Flex>
  )
}

/** ZIP の中の XML・CSV 以外で、 無視したファイルの件数。 */
export const ImportIgnoredNote = ({
  prefix,
  count,
}: {
  prefix: string
  count: number
}): React.ReactElement | null => {
  const { t } = useTranslation()
  if (count === 0) return null
  return (
    <Text textStyle="caption" color="Text.midEmphasis">
      {t(`${prefix}.ignored`, { count })}
    </Text>
  )
}
